import React from "react";
import { useLDClient } from "launchdarkly-react-client-sdk";

import { logger } from "@jslib/shared/utils/logger";

import { getOptions } from "../../options/options";

export const COLLECT_INFO_SUBMISSION_TIME = "collect-info-submission-time";
export const COLLECT_INFO_SUBMIT = "collect-info-submit";
export const DOC_UPLOAD_SUBMIT = "doc-upload-submit";
export const DOC_UPLOAD_SUBMISSION_TIME = "doc-upload-submission-time";

const createUniqueId = () => Math.random().toString(36).substr(2, 9);

export const MetricIds = {
  OrgSearchNumReworks: "org-search-num-reworks",
  OrgSearchQueryLength: "org-search-query-length",
  OrgSearchResultRank: "org-search-result-rank",
  OrgSearchTimeUntilSelection: "org-search-time-until-selection",
  OrgSearchOpenSortMenu: "org-search-open-sort-menu",
  OrgSearchSortDistance: "org-search-sort-distance",
  OrgSearchSortRelevance: "org-search-sort-relevance",
  AsrTipsShown: "asr-tips-shown",
  AsrSearchAfterTips: "asr-search-after-tips",
  AsrClickThrough: "asr-click-through",
  CaptchaUnmountedBeforeRender: "captcha-unmounted-before-render",
  CaptchaAbandonedAfterRender: "captcha-abandoned-after-render",
  CaptchaRendered: "captcha-rendered",
  CaptchaVerified: "captcha-verified",
  CaptchaErrorClient: "captcha-error-client",
  CaptchaErrorClientCode: "captcha-error-client-code",
  CaptchaErrorScriptLoad: "captcha-error-script-load",
  CaptchaErrorServer: "captcha-error-server",
  CaptchaErrorBrowserCompatibility: "captcha-error-browser-compatibility",
  CaptchaRetryClicked: "captcha-retry-clicked",
  CaptchaErrorLoadTimeout: "captcha-error-load-timeout",
} as const;

type MetricId = (typeof MetricIds)[keyof typeof MetricIds];

export const useMetrics = ({ segment, programId }: { segment?: string; programId?: string }) => {
  const [randomId] = React.useState(createUniqueId());
  const ldClient = useLDClient();

  React.useEffect(() => {
    // set mark when this component first renders
    try {
      performance.mark(randomId);
    } catch {
      // do nothing if performance.mark errors (this happens in tests because jsdom)
    }
  }, []);

  return {
    // @TODO move GA and NR events into this?
    userDidConvert: (metricIdConverted: string, metricIdSubmitTime: string): void => {
      if (getOptions()._launchDarklyUserTargeting) {
        try {
          const timeOnForm = performance.measure("timeOnForm", randomId);
          ldClient.track(metricIdConverted, {
            segment,
          });

          ldClient.track(
            metricIdSubmitTime,
            {
              segment,
            },
            timeOnForm.duration,
          );
        } catch (e) {
          logger.error("Unable to submit metrics", e);
        }
      }
    },
    recordNumericMetric: (metricId: MetricId, value: number): void => {
      if (getOptions()._launchDarklyUserTargeting) {
        ldClient.track(
          metricId,
          {
            segment,
            programId,
          },
          value,
        );
      }
    },
    trackEvent: (metricId: MetricId): void => {
      if (getOptions()._launchDarklyUserTargeting) {
        ldClient.track(metricId, {
          segment,
          programId,
        });
      }
    },
    flush: (): void => {
      ldClient.flush();
    },
    /**
     * @deprecated use userDidConvert instead
     */
    getTimeOnForm: (): PerformanceMeasure => {
      try {
        return performance.measure("timeOnForm", randomId);
      } catch (e) {
        // do nothing if performance.mark errors (this happens in tests because jsdom)
      }

      return undefined;
    },
  };
};
